import { EventEmitter } from '@spotify-internal/emitter';
import { Errors } from './enums/errors';
import { OperationResult } from './enums/operation_result';
import { RepeatMode } from './enums/repeat_mode';
import { ListConstants } from './enums/list_constants';
import { Reason } from './enums/reason';
import { Event } from './enums/event';
import { PlaybackError } from './error/playback';
import { defer } from './_internal/defer';
function createPlaybackOptions(options = {}) {
    const overriddenOptions = {
        index: -1,
        position: 0,
        initialPosition: 0,
        duration: -1,
        paused: false,
        reason: Reason.UNKNOWN,
    };
    if (typeof options.index !== 'undefined') {
        overriddenOptions.index = options.index;
    }
    if (typeof options.position !== 'undefined') {
        overriddenOptions.position = options.position;
    }
    if (typeof options.initialPosition !== 'undefined') {
        overriddenOptions.initialPosition = options.initialPosition;
    }
    if (typeof options.duration !== 'undefined') {
        overriddenOptions.duration = options.duration;
    }
    if (typeof options.paused !== 'undefined') {
        overriddenOptions.paused = options.paused;
    }
    if (typeof options.reason !== 'undefined') {
        overriddenOptions.reason = options.reason;
    }
    return overriddenOptions;
}
function isTrack(track) {
    return 'uri' in track;
}
/**
 * The maximum value a sequence number can have.
 */
const MAX_SEQ_ID = 9007199254740991;
/**
 * The default number of errors before the list player stops progressing to the
 * next track.
 */
const DEFAULT_MAX_LIST_ERRORS = 5;
export class ListPlayer extends EventEmitter {
    /**
     * A ListPlayer manages playing through "smartlists."
     *
     * @param options - The options for this instance of the ListPlayer.
     */
    constructor(options) {
        super();
        /**
         * Used to ensure that the track player is present before any operations.
         */
        this._trackPlayerPromise = defer();
        this._maxListErrors = DEFAULT_MAX_LIST_ERRORS;
        /**
         * An incrementing ID for the currently playing track.
         * This value can be used to compare whether the tracks have changed.
         */
        this._uid = 0;
        /**
         * The list that is loaded into the ListPlayer.
         * All operations happen on this list.
         */
        this._loadedList = null;
        /**
         * The PlaybackOptions for the currently loaded list.
         */
        this._loadedOptions = null;
        /**
         * The currently loaded track.
         */
        this._currentTrack = null;
        /**
         * The calculated playback options for the current track.
         */
        this._currentTrackOptions = null;
        /**
         * The amount of items played from the current list.
         */
        this._listPlayCount = 0;
        /**
         * Number of times that the list has produced a playback error.
         */
        this._listErrorCount = 0;
        /**
         * An internal flag denoting that the ListPlayer should shuffle all lists it
         * plays.
         */
        this._shuffled = false;
        /**
         * An internal flag denoting the repeat mode of the ListPlayer.
         */
        this._repeatMode = RepeatMode.NONE;
        /**
         * Used to keep track of asynchronous operations that span multiple execution
         * contexts.
         *
         * Note that this value should not be incremented directly. Use the method
         * _incrementSeqId() instead.
         *
         * Every time an operation requiring multiple asynchronous calls is done, we
         * increment this value. During the difference stages in the async operation,
         * we check whether the value has changed. If it has, we assume that the
         * operation was cancelled, and we do not proceed with the rest of the
         * operation.
         */
        this._currentSeqId = 0;
        if (!options.trackPlayer) {
            throw new TypeError('Argument `options.trackPlayerManager` not found.');
        }
        if (options.maxListErrors) {
            this._maxListErrors = options.maxListErrors;
        }
        // Rebind
        this._setListOptions = this._setListOptions.bind(this);
        this._replaceCurrentList = this._replaceCurrentList.bind(this);
        // Rebind event handlers
        this._handleBeforeVolumeChange = this._handleBeforeVolumeChange.bind(this);
        this._handleCapped = this._handleCapped.bind(this);
        this._handlePlaying = this._handlePlaying.bind(this);
        this._handlePaused = this._handlePaused.bind(this);
        this._handleEnded = this._handleEnded.bind(this);
        this._handleCanPreload = this._handleCanPreload.bind(this);
        this._handleError = this._handleError.bind(this);
        this._handlePositionChanged = this._handlePositionChanged.bind(this);
        this._handleDurationChanged = this._handleDurationChanged.bind(this);
        this._handleTimeout = this._handleTimeout.bind(this);
        this._handlePlayerInitError = this._handlePlayerInitError.bind(this);
        this._handlePlayedThresholdReached = this._handlePlayedThresholdReached.bind(this);
        this._init(options.trackPlayer);
    }
    static create(options) {
        return new ListPlayer(options);
    }
    _init(trackPlayer) {
        // Ensure that there's at least 1 listener to trackPlayerPromise so we don't
        // get errors in the dev tools.
        this._trackPlayerPromise.promise.catch(() => {
            /* NO OP */
        });
        // Track Player
        Promise.resolve(trackPlayer)
            .then((player) => {
            this._attachPlayerEvents(player);
            this._trackPlayerPromise.resolve(player);
        })
            .catch(this._handlePlayerInitError);
    }
    _attachPlayerEvents(player) {
        player.on(Event.PLAYER_BEFORE_VOLUME_CHANGE, this._handleBeforeVolumeChange);
        player.on(Event.PLAYER_CAN_PRELOAD, this._handleCanPreload);
        player.on(Event.PLAYER_CAPPED, this._handleCapped);
        player.on(Event.PLAYER_ENDED, this._handleEnded);
        player.on(Event.PLAYER_ERROR, this._handleError);
        player.on(Event.PLAYER_PAUSED, this._handlePaused);
        player.on(Event.PLAYER_PLAYING, this._handlePlaying);
        player.on(Event.PLAYER_TIMEOUT, this._handleTimeout);
        player.on(Event.PLAYER_POSITION_CHANGED, this._handlePositionChanged);
        player.on(Event.PLAYER_DURATION_CHANGED, this._handleDurationChanged);
        player.on(Event.PLAYER_PLAYED_THRESHOLD_REACHED, this._handlePlayedThresholdReached);
        // Proxy Events
        this.proxyEmit(player, Event.PLAYER_PROGRESS, Event.LIST_PLAYER_PROGRESS);
        this.proxyEmit(player, Event.PLAYER_AUTOPLAY_FAILED, Event.LIST_PLAYER_AUTOPLAY_FAILED);
        this.proxyEmit(player, Event.PLAYER_BEFORE_LOAD, Event.LIST_PLAYER_BEFORE_PLAYER_LOAD);
        this.proxyEmitSync(player, Event.PLAYER_LOAD, Event.LIST_PLAYER_PLAYER_LOAD);
        this.proxyEmit(player, Event.PLAYER_TRACKING_DATA_CREATED, Event.LIST_PLAYER_TRACKING_DATA_CREATED);
        this.proxyEmit(player, Event.PLAYER_TRACKING_DATA_FINALIZED, Event.LIST_PLAYER_TRACKING_DATA_FINALIZED);
        this.proxyEmit(player, Event.PLAYER_BUFFER_STALLED, Event.LIST_PLAYER_BUFFER_STALLED);
        this.proxyEmit(player, Event.PLAYER_BUFFERING_START, Event.LIST_PLAYER_BUFFERING_START);
        this.proxyEmit(player, Event.PLAYER_BUFFERING_END, Event.LIST_PLAYER_BUFFERING_END);
        this.proxyEmit(player, Event.PLAYER_VIDEO_ELEMENT_APPENDED, Event.LIST_PLAYER_VIDEO_ELEMENT_APPENDED);
        this.proxyEmit(player, Event.PLAYER_VIDEO_ELEMENT_REMOVED, Event.LIST_PLAYER_VIDEO_ELEMENT_REMOVED);
    }
    _handlePlayerInitError(error) {
        this._trackPlayerPromise.reject(new PlaybackError(Errors.LIST_PLAYER_NO_TRACK_PLAYER, error.message || 'Track player promise was rejected.'));
    }
    _handleCapped() {
        const currentTrack = this._currentTrack;
        if (!currentTrack) {
            return;
        }
        this.emit(Event.LIST_PLAYER_CAPPED, {
            uid: this._uid,
            track: currentTrack,
            options: this._currentTrackOptions,
            list: this._loadedList,
        });
    }
    _handlePlaying(e) {
        const currentTrack = this._currentTrack;
        if (!currentTrack) {
            return;
        }
        this.emit(Event.LIST_PLAYER_PLAYING, {
            uid: this._uid,
            track: currentTrack,
            options: this._currentTrackOptions,
            list: this._loadedList,
            position: e.data.position,
        });
    }
    _handlePaused(e) {
        const currentTrack = this._currentTrack;
        if (!currentTrack) {
            return;
        }
        this.emit(Event.LIST_PLAYER_PAUSED, {
            uid: this._uid,
            track: currentTrack,
            options: this._currentTrackOptions,
            list: this._loadedList,
            position: e.data.position,
        });
    }
    // FIXME: Use proxyEmit instead of handling this explicitly.
    _handleBeforeVolumeChange(e) {
        const ev = this.emitSync(Event.LIST_PLAYER_BEFORE_VOLUME_CHANGE, {
            volume: e.data.volume,
        });
        if (ev.defaultPrevented) {
            e.preventDefault();
        }
    }
    /**
     * Callback for when the currently playing track is near the end.
     */
    _handleCanPreload() {
        this._preloadUpcomingTrack().catch(() => {
            // Noop
        });
    }
    _handleEnded() {
        const currentTrack = this._currentTrack;
        if (!currentTrack) {
            return;
        }
        this.emit(Event.LIST_PLAYER_TRACK_ENDED, {
            uid: this._uid,
            track: currentTrack,
            options: this._currentTrackOptions,
            list: this._loadedList,
        });
        this.next(Reason.TRACK_DONE);
    }
    _handleTimeout() {
        const currentTrack = this._currentTrack;
        if (!currentTrack) {
            return;
        }
        this.emit(Event.LIST_PLAYER_TRACK_TIMEOUT, {
            uid: this._uid,
            track: currentTrack,
            options: this._currentTrackOptions,
            list: this._loadedList,
        });
        this.next(Reason.TRACK_ERROR);
    }
    _handlePositionChanged(event) {
        const currentTrack = this._currentTrack;
        if (!currentTrack) {
            return;
        }
        this.emit(Event.LIST_PLAYER_POSITION_CHANGED, {
            uid: this._uid,
            track: currentTrack,
            options: this._currentTrackOptions,
            list: this._loadedList,
            position: event.data.position,
        });
    }
    _handleDurationChanged(event) {
        const currentTrack = this._currentTrack;
        if (!currentTrack) {
            return;
        }
        this.emit(Event.LIST_PLAYER_DURATION_CHANGED, {
            uid: this._uid,
            track: currentTrack,
            options: this._currentTrackOptions,
            list: this._loadedList,
            position: event.data.position,
            duration: event.data.duration,
        });
    }
    _handlePlayedThresholdReached(e) {
        const currentTrack = this._currentTrack;
        if (!currentTrack) {
            return;
        }
        this.emit(Event.LIST_PLAYER_PLAYED_THRESHOLD_REACHED, {
            uid: this._uid,
            track: currentTrack,
            options: this._currentTrackOptions,
            list: this._loadedList,
            threshold: e.data.threshold,
            position: e.data.position,
        });
    }
    _handleError(event) {
        const currentTrack = this._currentTrack;
        const data = event.data;
        if (!currentTrack) {
            return;
        }
        this.emit(Event.LIST_PLAYER_ERROR, data);
        this.emitSync(Event.LIST_PLAYER_ERROR_SYNC, data);
        // This will be set in cases where errors are expected to not increment the
        // list error count, and to by pass the error count check. This happens in the
        // case of ads, where an error should not prevent moving on to the next track.
        const ignoreError = !!data.error && data.error.listPlayerIgnore;
        if (!ignoreError) {
            this._listErrorCount++;
        }
        if (!data.canPlayNext) {
            return;
        }
        if (ignoreError || this._listErrorCount <= this._maxListErrors) {
            this.next(Reason.TRACK_ERROR);
        }
        else {
            this.emit(Event.LIST_PLAYER_MAX_LIST_ERRORS_REACHED, {
                count: this._listErrorCount,
                threshold: this._maxListErrors,
            });
        }
    }
    _handleTrackLoaded(data) {
        this.emitSync(Event.LIST_PLAYER_TRACK_LOADED, data);
    }
    /**
     * Increments the internal _seqId value.
     *
     * @return The new seqId value.
     */
    _incrementSeqId() {
        if (this._currentSeqId >= MAX_SEQ_ID) {
            this._currentSeqId = 0;
        }
        else {
            this._currentSeqId += 1;
        }
        return this._currentSeqId;
    }
    _getTrackPlayer() {
        return this._trackPlayerPromise.promise;
    }
    _setListIndex(list, options) {
        const index = !options.index || options.index === -1 ? 0 : options.index;
        return Promise.all([list, options, list.startAt(index)]);
    }
    _setListOptions([list, options, _operationResult]) {
        return Promise.all([
            list.setShuffle(!!this._shuffled),
            list.setRepeatMode(this._repeatMode),
        ]).then(() => [list, options]);
    }
    _replaceCurrentList([list, options]) {
        this.emitSync(Event.LIST_PLAYER_BEFORE_LIST_CHANGE, {
            newList: list,
            newOptions: options,
            oldList: this._loadedList,
            oldOptions: this._loadedOptions,
        });
        this._loadedList = list;
        this._loadedOptions = options;
        this._listPlayCount = 0;
        this._listErrorCount = 0;
        this.emit(Event.LIST_PLAYER_LIST_CHANGED, {
            list: list,
            options: options,
        });
        return OperationResult.SUCCESS;
    }
    _preloadTrack(track) {
        return this._getTrackPlayer().then((trackPlayer) => {
            trackPlayer.preload(track).catch(() => {
                // Preload failed. Ignore.
            });
            return OperationResult.SUCCESS;
        });
    }
    _changeTrack(track, reason, seqId) {
        if (this._currentSeqId !== seqId) {
            return Promise.resolve(OperationResult.CANCELLED);
        }
        this.emitSync(Event.LIST_PLAYER_BEFORE_TRACK_LOAD, {
            list: this._loadedList,
            newTrack: track,
            oldTrack: this._currentTrack,
        });
        this._uid++;
        const playCount = ++this._listPlayCount;
        // Calculate play options
        const options = this._loadedOptions;
        let autoplay = true;
        let position = 0;
        let playedThreshold = 30000;
        if (options) {
            if (playCount === 1) {
                autoplay = !options.paused;
                position = options.initialPosition || options.position || 0;
            }
            else {
                position = options.position || 0;
            }
        }
        // This option controls the "pausing" behaviour for any previously loaded
        // tracks from the TrackPlayer. When set to true, it'll continue playing
        // the previous track until the next track is loaded. This is the behaviour
        // necessary for preloading.
        const continuePrevious = reason === Reason.TRACK_DONE;
        // If the track has override options, override any of the variable values.
        if ('options' in track) {
            const overrideOptions = track.options;
            if ('paused' in overrideOptions) {
                autoplay = !overrideOptions.paused;
            }
            if ('position' in overrideOptions) {
                position = overrideOptions.position;
            }
            if ('playedThreshold' in overrideOptions) {
                playedThreshold = overrideOptions.playedThreshold;
            }
        }
        this._currentTrack = track;
        this._currentTrackOptions = {
            reason: reason,
            paused: !autoplay,
            position: position,
            playedThreshold: playedThreshold,
        };
        const handleTrackLoaded = this._handleTrackLoaded.bind(this, {
            uid: this._uid,
            track: this._currentTrack,
            options: this._currentTrackOptions,
            list: this._loadedList,
        });
        return this._getTrackPlayer().then((trackPlayer) => {
            if (this._currentSeqId !== seqId) {
                return OperationResult.CANCELLED;
            }
            Promise.resolve(trackPlayer.load(track, {
                uriProperty: 'playableURI',
                autoplay: autoplay,
                position: position,
                playedThreshold: playedThreshold,
                continuePrevious: continuePrevious,
            }, handleTrackLoaded)).catch(() => {
                // Ignore, as the events should give us information on errors, if any.
            });
            return OperationResult.SUCCESS;
        });
    }
    _preloadUpcomingTrack() {
        const list = this._loadedList;
        if (!list) {
            return Promise.reject(new PlaybackError(Errors.LIST_PLAYER_NO_LIST, 'Cannot perform operation; no list was loaded.'));
        }
        const opParams = {
            reason: Reason.TRACK_DONE,
            listConstants: ListConstants,
        };
        return list.peekNext(opParams).then((track) => {
            if (track === ListConstants.FORBIDDEN) {
                return OperationResult.FORBIDDEN;
            }
            if (track === ListConstants.NULL_VALUE) {
                return this._preloadUpcomingTrack();
            }
            if (track === ListConstants.LIST_END) {
                return OperationResult.LIST_END;
            }
            if (!isTrack(track)) {
                return OperationResult.INVALID;
            }
            if (!track.playable) {
                return OperationResult.INVALID;
            }
            return this._preloadTrack(track);
        });
    }
    activateElement() {
        return this._getTrackPlayer().then((player) => {
            player.activateElement();
            return OperationResult.SUCCESS;
        });
    }
    load(list, options) {
        const userOptions = options || this._loadedOptions || {};
        const mergedOptions = createPlaybackOptions(userOptions);
        return this._setListIndex(list, mergedOptions)
            .then(this._setListOptions)
            .then(this._replaceCurrentList);
    }
    play(list, options) {
        return this.load(list, options).then(() => {
            // If the list has changed, ignore the operation.
            if (this._loadedList !== list) {
                return OperationResult.CANCELLED;
            }
            const reason = this._loadedOptions
                ? this._loadedOptions.reason
                : Reason.UNKNOWN;
            return this.next(reason);
        });
    }
    canChangeTrack() {
        if (!this._loadedList) {
            return Promise.resolve(true);
        }
        return this._loadedList
            .peekNext({
            reason: Reason.FORWARD_BUTTON,
            listConstants: ListConstants,
        })
            .then((track) => track !== ListConstants.FORBIDDEN);
    }
    next(reason) {
        if (!reason) {
            return Promise.reject(new PlaybackError(Errors.LIST_PLAYER_INVALID_ARGUMENT, 'The argument `reason` is required.'));
        }
        const list = this._loadedList;
        if (!list) {
            return Promise.reject(new PlaybackError(Errors.LIST_PLAYER_NO_LIST, 'Cannot perform operation; no list was loaded.'));
        }
        this.emitSync(Event.LIST_PLAYER_BEFORE_NEXT, {
            list: list,
            reason: reason,
        });
        const seqId = this._incrementSeqId();
        const opParams = {
            reason: reason,
            listConstants: ListConstants,
        };
        return list.next(opParams).then((track) => {
            if (this._currentSeqId !== seqId) {
                return OperationResult.CANCELLED;
            }
            if (track === ListConstants.FORBIDDEN) {
                return OperationResult.FORBIDDEN;
            }
            if (track === ListConstants.NULL_VALUE) {
                return this.next(reason);
            }
            if (track === ListConstants.LIST_END) {
                this.emit(Event.LIST_PLAYER_LIST_ENDED, {
                    list: list,
                    reason: Reason.END_PLAY,
                });
                this.clear(reason);
                return OperationResult.LIST_END;
            }
            if (!isTrack(track)) {
                return OperationResult.INVALID;
            }
            if (!track.playable) {
                this.emit(Event.LIST_PLAYER_TRACK_UNPLAYABLE, {
                    track: track,
                    list: list,
                });
                return this.next(reason);
            }
            return this._changeTrack(track, reason, seqId);
        });
    }
    previous(reason) {
        if (!reason) {
            return Promise.reject(new PlaybackError(Errors.LIST_PLAYER_INVALID_ARGUMENT, 'The argument `reason` is required.'));
        }
        const list = this._loadedList;
        if (!list) {
            return Promise.reject(new PlaybackError(Errors.LIST_PLAYER_NO_LIST, 'Cannot perform operation; no list was loaded.'));
        }
        this.emitSync(Event.LIST_PLAYER_BEFORE_PREVIOUS, {
            list: list,
            reason: reason,
        });
        const seqId = this._incrementSeqId();
        const opParams = {
            reason: reason,
            listConstants: ListConstants,
        };
        return list.previous(opParams).then((track) => {
            if (this._currentSeqId !== seqId) {
                return OperationResult.CANCELLED;
            }
            if (track === ListConstants.FORBIDDEN) {
                return OperationResult.FORBIDDEN;
            }
            if (track === ListConstants.NULL_VALUE) {
                return this.previous(reason);
            }
            if (track === ListConstants.LIST_START) {
                this.emit(Event.LIST_PLAYER_LIST_ENDED, {
                    list: list,
                    reason: Reason.END_PLAY,
                });
                this.clear(reason);
                return OperationResult.LIST_END;
            }
            if (!isTrack(track)) {
                return OperationResult.INVALID;
            }
            if (!track.playable) {
                this.emit(Event.LIST_PLAYER_TRACK_UNPLAYABLE, {
                    track: track,
                    list: list,
                });
                return this.previous(reason);
            }
            return this._changeTrack(track, reason, seqId);
        });
    }
    pause() {
        if (!this._loadedList) {
            return Promise.reject(new PlaybackError(Errors.LIST_PLAYER_NO_LIST, 'Cannot perform operation; no list was loaded.'));
        }
        if (this._currentTrack) {
            return this._getTrackPlayer().then((trackPlayer) => {
                trackPlayer.pause();
                return OperationResult.SUCCESS;
            });
        }
        return Promise.resolve(OperationResult.SUCCESS);
    }
    resume() {
        if (!this._loadedList) {
            return Promise.reject(new PlaybackError(Errors.LIST_PLAYER_NO_LIST, 'Cannot perform operation; no list was loaded.'));
        }
        if (this._currentTrack) {
            return this._getTrackPlayer().then((trackPlayer) => {
                trackPlayer.resume();
                return OperationResult.SUCCESS;
            });
        }
        return Promise.resolve(OperationResult.SUCCESS);
    }
    togglePlay() {
        if (!this._loadedList) {
            return Promise.reject(new PlaybackError(Errors.LIST_PLAYER_NO_LIST, 'Cannot perform operation; no list was loaded.'));
        }
        if (this._currentTrack) {
            return this._getTrackPlayer().then((trackPlayer) => {
                trackPlayer.togglePlay();
                return OperationResult.SUCCESS;
            });
        }
        return Promise.resolve(OperationResult.SUCCESS);
    }
    stop(reason) {
        const logData = { reason: reason || Reason.UNKNOWN };
        const currentTrack = this._currentTrack;
        if (currentTrack && currentTrack.logData) {
            logData.source = currentTrack.logData.source;
        }
        this._incrementSeqId();
        return this._getTrackPlayer().then((trackPlayer) => {
            trackPlayer.stop(logData);
            this.emit(Event.LIST_PLAYER_STOPPED, {
                uid: this._uid,
                options: this._currentTrackOptions,
                list: this._loadedList,
            });
            return OperationResult.SUCCESS;
        });
    }
    clear(reason) {
        return this.stop(reason).then(() => {
            this._loadedList = null;
            this._loadedOptions = null;
            this._currentTrack = null;
            this._currentTrackOptions = null;
            this.emit(Event.LIST_PLAYER_CLEARED, null);
            return OperationResult.SUCCESS;
        });
    }
    setShuffle(shuffle) {
        // Ensure boolean
        const shuffled = !!shuffle;
        if (this._shuffled !== shuffled) {
            this._shuffled = shuffled;
            this.emit(Event.LIST_PLAYER_SHUFFLE_CHANGED, {
                player: this,
                shuffled: shuffled,
            });
            const list = this._loadedList;
            if (list) {
                list.setShuffle(this._shuffled);
            }
        }
        return Promise.resolve(OperationResult.SUCCESS);
    }
    setRepeatMode(repeatMode) {
        if (!(repeatMode in RepeatMode)) {
            return Promise.reject(new PlaybackError(Errors.LIST_PLAYER_INVALID_ARGUMENT, 'The value of repeat mode is not a correct RepeatMode enum value'));
        }
        if (this._repeatMode !== repeatMode) {
            this._repeatMode = repeatMode;
            this.emit(Event.LIST_PLAYER_REPEAT_MODE_CHANGED, {
                player: this,
                repeatMode: repeatMode,
            });
            const list = this._loadedList;
            if (list) {
                list.setRepeatMode(this._repeatMode);
            }
        }
        return Promise.resolve(OperationResult.SUCCESS);
    }
    getVolume() {
        return this._getTrackPlayer().then((player) => player.getVolume());
    }
    setVolume(volume, commandId = null) {
        return this._getTrackPlayer().then((player) => {
            player.setVolume(volume);
            this.emit(Event.LIST_PLAYER_VOLUME_CHANGED, {
                volume: volume,
                commandId: commandId,
            });
            return OperationResult.SUCCESS;
        });
    }
    seek(value) {
        const loadedList = this._loadedList;
        if (!loadedList) {
            return Promise.reject(new PlaybackError(Errors.LIST_PLAYER_NO_LIST, 'Cannot perform operation; no list was loaded.'));
        }
        if (typeof loadedList.allowSeeking === 'function' &&
            !loadedList.allowSeeking()) {
            return Promise.reject(new PlaybackError(Errors.LIST_PLAYER_FORBIDDEN, 'The operation is not allowed.'));
        }
        if (this._currentTrack) {
            return this._getTrackPlayer().then((trackPlayer) => {
                trackPlayer.seek(value);
                return OperationResult.SUCCESS;
            });
        }
        return Promise.resolve(OperationResult.SUCCESS);
    }
    getListConstants() {
        return ListConstants;
    }
    getLoadedList() {
        return this._loadedList;
    }
    getLoadedOptions() {
        return this._loadedOptions;
    }
    getPlayerState() {
        return this._getTrackPlayer().then((trackPlayer) => trackPlayer.getPlayerState());
    }
    getState() {
        return this.getPlayerState().then((state) => {
            return {
                playbackState: state,
                track: this._currentTrack,
                list: this._loadedList,
                options: this._loadedOptions,
            };
        });
    }
    getMediaConfig() {
        return this._getTrackPlayer().then((player) => player.getMediaConfig());
    }
}
//# sourceMappingURL=listplayer.js.map